import { eq, useLiveQuery as useTanstackLiveQuery } from "@tanstack/react-db";
import { useMemo } from "react";
import { membershipCollection } from "@/lib/collections/membership";
import type { Staff } from "@/lib/collections/staff";
import { staffCollection } from "@/lib/collections/staff";

/**
 * Resolves a staff caller's `membershipId` to their staff profile, for use
 * in the incoming-call UI when the invite came from another staff member
 * (PendingInvite.kind === "staff"). The membership row gives us the
 * `userId`, which keys into the staff collection.
 *
 * Returns `staff: null` while loading or when no match — mirrors
 * `useCallerLookup` so the IncomingCall UI can fall back to the name the
 * call carried, or "Internal call", until the join resolves.
 */
interface StaffCallerLookup {
  staff: Staff | null;
}

export function useStaffCallerLookup(
  staffMemberId: string | null | undefined
): StaffCallerLookup {
  // Membership row → userId. Filter is keyed by membership id (the value
  // we decoded out of the Twilio identity).
  const { data: memberships = [] } = useTanstackLiveQuery(
    (q) =>
      q
        .from({ m: membershipCollection })
        .where(({ m }) => eq(m.id, staffMemberId ?? "__no_match__"))
        .select(({ m }) => m),
    [staffMemberId]
  );

  const userId = memberships[0]?.userId ?? null;

  const { data: staffRows = [] } = useTanstackLiveQuery(
    (q) =>
      q
        .from({ s: staffCollection })
        .where(({ s }) => eq(s.id, userId ?? "__no_match__"))
        .select(({ s }) => s),
    [userId]
  );

  const staff = useMemo<Staff | null>(() => {
    const row = staffRows[0] as Staff | undefined;
    return row ?? null;
  }, [staffRows]);

  return { staff };
}
